import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiOrigin } from '../lib/api';
import participantsService from '../services/participantsService';
import Skeleton from '../components/Skeleton';

export default function ClientDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = useRef(false);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Parse slug into base name and short token
  const parts = String(slug || '').split('-');
  const last = parts[parts.length - 1] || '';
  const hasShort = /^[a-f0-9]{12}$/i.test(last);
  const base = hasShort ? parts.slice(0, -1).join('-') : String(slug || '');

  // Helper: reconstruct UUID from 12-hex token using dataset convention
  function fullUuidFromShort(short) {
    const s = String(short || '').toLowerCase();
    if (!/^[a-f0-9]{12}$/.test(s)) return '';
    const first8 = s.slice(0, 8);
    const last4 = s.slice(-4);
    return `${first8}-0000-0000-0000-00000000${last4}`;
  }

  // Helper: derive 12-hex short token from a UUID (first 8 + last 4)
  function shortFromUuid(uuid) {
    const clean = String(uuid || '').replace(/-/g, '').toLowerCase();
    if (!/^[a-f0-9]{32}$/.test(clean)) return '';
    return `${clean.slice(0, 8)}${clean.slice(-4)}`;
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!slug) { setError('Missing client identifier'); return; }
        setLoading(true);
        setError(null);

        let user = null;

        // Resolution strategy for client (buyer) public profile:
        // 1) If token present, reconstruct UUID and fetch public user info
        if (!user && hasShort) {
          const uuid = fullUuidFromShort(last);
          if (uuid) {
            try {
              const arr = await participantsService.getPublicUsers(uuid);
              const u = Array.isArray(arr) ? arr[0] : null;
              if (u && u.id) user = u;
            } catch (e) { void e; }
          }
        }

        // 2) If still not found, attempt name-only fallback is not available via API
        // For now, if no token, we consider not resolvable and show Not found.

        if (mounted && user) {
          setItem(user);
          // Canonicalize only prefix and token presence; do NOT rename based on token
          const token = hasShort ? last.toLowerCase() : shortFromUuid(user?.id);
          const prefix = (location?.pathname || '').startsWith('/clients/profile/') ? '/clients/profile/' : '/client/profile/';
          const baseFromUrl = String(base || '').toLowerCase();
          const canonicalPath = token ? `${prefix}${baseFromUrl}-${token}` : `${prefix}${baseFromUrl}`;
          const currentPrefix = (location?.pathname || '').startsWith('/clients/profile/') ? '/clients/profile/' : ((location?.pathname || '').startsWith('/client/profile/') ? '/client/profile/' : '');
          const shouldRedirect = (!hasShort && !!token) || (currentPrefix !== prefix);
          if (shouldRedirect && !redirectedRef.current && location.pathname !== canonicalPath) {
            redirectedRef.current = true;
            navigate(canonicalPath, { replace: true });
          }
        }
      } catch {
        setError('Unable to load client details');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug, base, hasShort, last, location.pathname, navigate]);

  // SEO: canonical link (preserve current path to avoid unexpected renames)
  useEffect(() => {
    if (!item) return;
    try {
      const origin = window.location.origin || apiOrigin || '';
      const canonicalUrl = `${origin}${location.pathname}`;

      let linkTag = document.head.querySelector('link[rel="canonical"]');
      if (!linkTag) {
        linkTag = document.createElement('link');
        linkTag.setAttribute('rel', 'canonical');
        document.head.appendChild(linkTag);
      }
      linkTag.setAttribute('href', canonicalUrl);
      const maskedName = item?.name || '';
      document.title = `${maskedName} | Client Profile | LaLand`;
    } catch (e) { void e; }
  }, [item, hasShort, last, location.pathname]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="rect" className="h-56" />
        <Skeleton variant="rect" className="h-32" />
      </div>
    );
  }
  if (error) return <div className="text-sm text-red-400">{error}</div>;
  if (!item) return <div className="text-sm text-neutral-400">Not found</div>;

  const avatarUrl = item?.avatar_url || '/logo-kreativ-light.png';
  const displayName = item?.name || 'Client';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="rounded-2xl bg-secondary-bg border border-card-border p-6 flex items-center gap-4">
        <img src={avatarUrl} alt={`${displayName} - Client profile picture`} className="w-16 h-16 rounded-full object-cover border border-card-border" onError={(e)=>{e.currentTarget.src='/logo-kreativ-light.png';}} />
        <div>
          <h1 className="text-xl font-semibold text-primary-text">{displayName}</h1>
          <div className="text-xs text-secondary-text">Client profile</div>
        </div>
      </div>
      <div className="rounded-2xl bg-secondary-bg border border-card-border p-6">
        <div className="text-sm text-secondary-text">This is a public client profile. Detailed activity is private.</div>
      </div>
    </div>
  );
}
